// NOMBASE — Auth context.
//
// As of May 2026, no Clerk SDK runs in the browser. Identity is
// established server-side via `/api/auth/dev-bootstrap` minting an
// HS256 `nombase_session` cookie on the first 401 from
// `/api/auth/me`. The legacy Clerk Bearer JWT path in
// `backend/services/clerk_auth.py` remains wired for future
// re-introduction of a frontend Clerk SDK, but is dormant today.
//
// The previous gateway/satellite architecture was retired the same
// month because the cross-origin handshake produced an irreducible
// loop class of bugs. All gateway primitives
// (`__clerk_db_jwt`, `isSatellite`, `redirectToFrontDoor`,
// `return_to`, `GATEWAY_URL`) are gone — this context now reads
// identity from a single source (the backend cookie) and exposes the
// same shape to downstream consumers — `currentUser`, `currentTenant`,
// `isBrand`, `logout`, etc. — so no caller changes.
import {
  createContext, useContext, useState, useCallback,
  useEffect, useMemo, useRef,
} from 'react';
import * as Sentry from '@sentry/react';
import posthog from '@/lib/posthog';
import { authAPI } from '@/services/api';
import { resolvePersonaFlags } from '@/lib/personaResolution';
import { resolveGatewayUrl } from '@/lib/gatewayUrl';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Module-level "navigation pending" flag. Set as soon as
// `navigateToSignIn` calls `window.location.assign(...)`. Read by
// App.js's unauth `useEffect` to suppress a competing redirect.
//
// Why this exists: `logout()` runs roughly
//   setIsAuthed(false)            // queues React re-render
//   navigateToSignIn('sign_out')  // queues browser navigation to /sign-out
// `window.location.assign` is asynchronous — the browser commits it on
// the next event-loop tick, AFTER React has flushed the re-render. The
// re-render makes App.js's unauth `useEffect` fire, which calls
// `window.location.assign(gateway/sign-in)` — that second call lands
// AFTER the queued sign-out and wins, dropping the user on the sign-in
// page instead of the sign-out page. Setting this flag before the first
// assign() lets App.js skip its redirect when one is already pending,
// so the sign-out URL holds.
let _signinNavigationPending = false;
export function isSignInNavigationPending() {
  return _signinNavigationPending;
}

// `navigateToSignIn` is the single helper any sign-out / re-auth code
// path bounces through. Post-Gateway-reconnect (May 2026 v2) it
// resolves to the Nombase Gateway's sign-in surface with a `return_to`
// pointing at the current page so the user lands back where they
// were after a successful Clerk sign-in.
//
// Fallback (no `REACT_APP_GATEWAY_URL` configured): bounce to `/` so
// AuthContext.loadIdentity re-runs the dev_bootstrap path and the
// preview environment stays usable.
export function navigateToSignIn(reason = 'reauth') {
  if (typeof window === 'undefined') return;
  // Mark the navigation pending BEFORE any state-mutating callers
  // (e.g. logout's `setIsAuthed(false)`) have a chance to trigger a
  // competing redirect inside App.js's unauth `useEffect`.
  _signinNavigationPending = true;
  // Resolve the Gateway origin via the shared helper. Source is one of:
  //   'env'     — REACT_APP_GATEWAY_URL was valid + cross-origin (preview).
  //   'derived' — env was missing/self-pointing; we stripped `mrp.` from
  //               window.location.host (prod self-heal — see lib/gatewayUrl.js
  //               for the 2026-05-26 incident write-up).
  //   'none'    — neither path produced a Gateway URL; fall through to the
  //               in-app fallback below.
  const { url: gatewayUrl, source: gwSource } = resolveGatewayUrl();
  const rawEnv = (process.env.REACT_APP_GATEWAY_URL || '').replace(/\/+$/, '');

  // Loud-fail diagnostics ─ silently composing `undefined/sign-in` was
  // the root cause of the 2026-05-26 logout-loop bug. Surface every
  // resolution path so the next reader (human or agent) sees the
  // failure mode without having to grep React internals.
  if (!gatewayUrl) {
    if (!rawEnv) {
      // eslint-disable-next-line no-console
      console.error(
        '[navigateToSignIn] REACT_APP_GATEWAY_URL is empty AND host does'
        + ' not start with `mrp.` — falling back to MRP root. Set the env'
        + ' var in /app/frontend/.env (preview) or the deployment secrets'
        + ' panel + redeploy (prod). CRA bakes REACT_APP_* at build.'
      );
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `[navigateToSignIn] REACT_APP_GATEWAY_URL (${rawEnv}) points at`
        + ` THIS MRP origin (${window.location.origin}) AND host does not`
        + ' start with `mrp.` — runtime fallback impossible. Expected the'
        + ' Nombase Gateway host (e.g. https://nombaseconnect.com).'
        + ' Falling back to MRP root to avoid the same-origin redirect loop.'
      );
    }
  } else if (gwSource === 'derived') {
    // Soft-warn: the runtime fallback fired. Functional, but the operator
    // should still fix the Custom Keys / Secrets panel so the next build
    // bakes the right value and stops relying on the host-strip heuristic.
    // eslint-disable-next-line no-console
    console.warn(
      `[navigateToSignIn] REACT_APP_GATEWAY_URL was missing/self-pointing —`
      + ` derived Gateway from window.location: ${gatewayUrl}. Update prod`
      + ' Secrets to bake this at build time and silence this warning.'
    );
  }

  try {
    if (gatewayUrl) {
      // Prefer the sign-out endpoint when the reason is "sign_out" —
      // that drops the Clerk session at the Gateway too, so the user
      // doesn't get instantly silently signed back in. For any other
      // reason (session expired, forced re-auth, etc.) hit the
      // sign-in surface directly with a return_to.
      const isSignOut = reason === 'sign_out';
      const here = window.location.href;
      const u = new URL(
        isSignOut ? `${gatewayUrl}/sign-out` : `${gatewayUrl}/sign-in`,
      );
      // Sign-out returns the user to the Gateway root so they land on
      // the sign-in page next; sign-in carries the deep-link.
      u.searchParams.set('return_to', isSignOut ? gatewayUrl : here);
      window.location.assign(u.toString());
      return;
    }
    // No Gateway configured (or misconfigured) — preview/dev fallback.
    // Lands on MRP root which re-runs `loadIdentity` → dev_bootstrap
    // path on preview pods, or the static unauth card on prod pods
    // (since dev_bootstrap is hard-disabled there).
    const next = new URL('/', window.location.origin);
    if (reason) next.searchParams.set('reason', reason);
    window.location.assign(next.toString());
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[navigateToSignIn] redirect failed:', e);
    _signinNavigationPending = false; // allow App.js to retry
    window.location.assign('/');
  }
}

// ClerkBridge removed (May 2026). With Clerk fully ripped out of the
// frontend, there's no live `getToken` to push into the API token
// bridge and no post-mount re-probe to schedule. AuthProvider runs
// `loadIdentity` once on mount; the dev_bootstrap fallback handles
// the cookie minting. No bridge component needed.

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser]     = useState(null);
  const [currentTenant, setCurrentTenant] = useState(() => {
    try { const raw = localStorage.getItem('nombase_tenant'); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  const [isLoading, setIsLoading]         = useState(true);
  const [isAuthenticated, setIsAuthed]    = useState(false);
  // Captures the most-recent fatal auth-bootstrap failure (most
  // commonly a rejected Gateway SSO token). When non-null AND the URL
  // arrived with `?token=`, App.js suppresses the auto-redirect-to-
  // Gateway loop and shows the actual rejection reason. Stays null on
  // the happy path so consumers stay unaffected.
  const [authError, setAuthError]         = useState(null);

  // ── Sliding-expiry refresh scheduler ────────────────────────────
  // After a successful identity boot, we ping
  // `POST /api/auth/session/refresh` to reissue the `nombase_session`
  // cookie with a fresh `exp`. The backend returns `ttl_seconds`, and
  // we schedule the NEXT refresh at ~75 % of that window so an
  // actively-used tab effectively never needs to bounce back to
  // `/sign-in` for re-auth.
  //
  // Implementation rules (all to avoid the common pitfalls):
  //   • `setTimeout` id lives in a ref so React StrictMode's
  //     double-mount and any dependency-array changes don't leave a
  //     timer dangling.
  //   • `_refreshGenRef` ensures a stale in-flight refresh from a
  //     previous identity (e.g. user logged out, then logged back
  //     in) cannot resurrect state for the wrong user.
  //   • On a 401 from refresh we DO NOT retry — auth is gone, we
  //     just clear state and let the existing route guard send the
  //     user to the front door (no loop).
  //   • The scheduler bails immediately if `isAuthenticated` flips
  //     false (logout / forced unauth).
  const refreshTimerRef = useRef(null);
  const refreshGenRef   = useRef(0);   // bumped on every (un)auth flip
  const REFRESH_FRACTION_OF_TTL  = 0.75;
  const REFRESH_MIN_DELAY_MS     = 30 * 1000;          // never less than 30s
  const REFRESH_MAX_DELAY_MS     = 23 * 60 * 60 * 1000; // never more than 23h
  // (24h cookie ttl × 75% = 18h — well under the cap; the cap exists
  // for ops who configure NOMBASE_SESSION_TTL_SECONDS > 30h.)

  function _clearRefreshTimer() {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }

  // Perform a single refresh round-trip + schedule the next one.
  // `gen` is the identity generation that issued this call; if the
  // generation has advanced (user logged out / logged back in
  // before our network call returned), we drop the result on the
  // floor instead of mutating state for the wrong identity.
  async function _runRefreshOnce(gen) {
    let body = null;
    try {
      body = await authAPI.refreshSession();
    } catch (e) {
      // Stale gen → we're already on a different identity, abort.
      if (gen !== refreshGenRef.current) return;
      // 401 from refresh → session is gone. Wipe local state and
      // let the route guard in App.js navigate to `/sign-in`.
      // We DO NOT call navigateToSignIn() directly here because
      // (a) it would race with the App-level navigation and
      // (b) it would convert every transient backend hiccup into a
      // hard navigation.
      const msg = String(e?.message || '').toLowerCase();
      const looks401 = msg.includes('unauth')
                    || msg.includes('not authenticated')
                    || msg.includes('session expired')
                    || msg.includes('no session');
      if (looks401) {
        _clearRefreshTimer();
        setCurrentUser(null);
        setCurrentTenant(null);
        setIsAuthed(false);
        try { localStorage.removeItem('nombase_tenant'); } catch { /* */ }
        return;
      }
      // Non-401 (network blip / 5xx). Don't tear down state — just
      // try again after a conservative delay so we don't pile on
      // a struggling backend.
      _scheduleNextRefresh(gen, /* ttl_seconds */ 5 * 60);
      return;
    }

    if (gen !== refreshGenRef.current) return; // stale

    // Success — update user if the backend returned a fresh shape
    // (tenant/role changes propagate without a page reload).
    if (body && body.user && body.user.id) {
      setCurrentUser(body.user);
    }
    _scheduleNextRefresh(gen, Number(body?.ttl_seconds) || 0);
  }

  function _scheduleNextRefresh(gen, ttlSeconds) {
    _clearRefreshTimer();
    // 0 / NaN ttl → backend didn't tell us when the cookie expires.
    // Fall back to a 15 min poll so we still notice an externally-
    // revoked session reasonably quickly.
    const baseMs = (Number.isFinite(ttlSeconds) && ttlSeconds > 0)
      ? Math.round(ttlSeconds * 1000 * REFRESH_FRACTION_OF_TTL)
      : 15 * 60 * 1000;
    const delayMs = Math.max(REFRESH_MIN_DELAY_MS, Math.min(baseMs, REFRESH_MAX_DELAY_MS));
    refreshTimerRef.current = setTimeout(() => {
      if (gen !== refreshGenRef.current) return;
      _runRefreshOnce(gen);
    }, delayMs);
  }

  // ── Bootstrap: ask backend who we are.
  //
  //    Auth: As of May 2026, no Clerk SDK runs in the browser.
  //    Identity is established server-side — the fetch wrapper
  //    sends `credentials: 'include'` so the `nombase_session`
  //    HttpOnly cookie (minted by `/api/auth/dev-bootstrap`) rides
  //    along on every request. The legacy `Authorization: Bearer`
  //    path remains accepted by the backend (see
  //    `backend/services/clerk_auth.py`) for a future reconnect,
  //    but is dormant today.
  //
  //    Gateway SSO hand-off (added when Gateway reconnect shipped):
  //    if the URL carries `?token=…`, we POST it to `/api/sso`
  //    BEFORE the existing /auth/me probe — the exchange endpoint
  //    sets the `nombase_session` cookie on its 302 response. After
  //    a successful exchange we scrub the `token` (and optional
  //    `return_to`) params from the address bar via
  //    `history.replaceState` so a refresh doesn't replay the
  //    one-shot token (jti would reject it, surfacing a confusing
  //    error in the unauth UI). On exchange failure we DROP through
  //    to the existing dev-bootstrap path — the bad-token signal is
  //    already logged server-side and the user shouldn't be locked
  //    out of preview environments by a stale URL.
  //
  //    If /auth/me 401s (no session, stale cookie evicted by browser
  //    hardening) we INLINE the dev-bootstrap retry here. Reloading
  //    the page to "let the cookie commit" is fragile — Chrome's
  //    cookie eviction policies, ITP, and the 180-cookie-per-domain
  //    cap can all silently drop the new cookie before the reload
  //    reads it. Posting + retrying in the same context shares the
  //    cookie jar synchronously and bypasses every reload race.
  const loadIdentity = useCallback(async () => {
    setIsLoading(true);
    refreshGenRef.current += 1;
    const myGen = refreshGenRef.current;
    _clearRefreshTimer();

    // Inner helper so the same logic runs on the initial probe and
    // (if needed) on the post-bootstrap retry. Returns the resolved
    // user or `null` if /auth/me 401s.
    async function _probeMe() {
      try {
        const m = await authAPI.getMe();
        return (m && m.id) ? m : null;
      } catch {
        return null;
      }
    }

    // Gateway SSO exchange — runs first. If we have a `?token=` in
    // the URL, exchange it for a cookie BEFORE probing /auth/me.
    // Successful exchange → skip the dev_bootstrap fallback (the
    // exchange already minted a real session). Failed exchange → fall
    // through to the normal probe → dev_bootstrap path. We always
    // scrub the param from the URL post-exchange so a refresh doesn't
    // replay a single-use token against the jti cache and confuse the
    // user with a `token_replayed` error.
    let exchangeAttempted = false;
    let exchangeSucceeded = false;
    let exchangeError = null;
    try {
      if (typeof window !== 'undefined' && window.location.search) {
        const sp = new URLSearchParams(window.location.search);
        const incomingToken = sp.get('token');
        if (incomingToken) {
          exchangeAttempted = true;
          try {
            await authAPI.ssoExchange(incomingToken);
            exchangeSucceeded = true;
          } catch (e) {
            exchangeSucceeded = false;
            exchangeError = (e && e.message) ? String(e.message) : 'sso_exchange_failed';
            // Loud-fail so the failure surfaces in the console without
            // requiring server logs the user can't access. Sentinel
            // prefix `[SSO]` makes it greppable for the next agent.
            // eslint-disable-next-line no-console
            console.error('[SSO] Gateway token exchange failed:', exchangeError);
          }
          // Scrub `token` (and `return_to` if present — it has done
          // its job server-side) from the address bar regardless of
          // exchange outcome.
          sp.delete('token');
          sp.delete('return_to');
          const qs = sp.toString();
          const clean = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
          try { window.history.replaceState({}, '', clean); } catch { /* */ }
        }
      }
    } catch { /* defensive — any URL-parsing failure falls through */ }

    try {
      let me = await _probeMe();

      // If the first probe failed AND no SSO exchange just succeeded,
      // mint a dev_bootstrap session and retry once. (Skip when SSO
      // already succeeded — a 401 there would mean the cookie was
      // dropped, and devBootstrap can't fix that.) Any failure from
      // devBootstrap (404 = not configured, 5xx = transient) just
      // falls through to the unauth state below — no exception
      // escapes the catch.
      if (!me && !exchangeSucceeded) {
        try {
          await authAPI.devBootstrap();
          // Brief microtask flush so the Set-Cookie header is processed
          // by the browser before the next fetch reads it.
          await new Promise(r => setTimeout(r, 50));
          me = await _probeMe();
        } catch {
          me = null;
        }
      }

      if (!me) {
        setCurrentUser(null);
        setCurrentTenant(null);
        setIsAuthed(false);
        try { localStorage.removeItem('nombase_tenant'); } catch { /* */ }
        // If a Gateway token came in AND we still couldn't auth, that's
        // a fatal exchange failure — surface it so App.js suppresses
        // the auto-loop-back-to-Gateway. We carry the backend's
        // `detail` slug verbatim (`token_expired`, `token_invalid`,
        // etc.) so the user sees actionable text, not a generic blank.
        if (exchangeAttempted && !exchangeSucceeded) {
          const diag = {
            ts: new Date().toISOString(),
            phase: 'sso_exchange',
            error: exchangeError || 'sso_exchange_failed',
            host: typeof window !== 'undefined' ? window.location.host : null,
          };
          setAuthError(diag);
          if (typeof window !== 'undefined') {
            try { window.__nombase_diag__ = diag; } catch { /* */ }
          }
        } else {
          setAuthError(null);
        }
        return;
      }
      // Clear any prior auth error on a successful identity load.
      setAuthError(null);

      setCurrentUser(me);
      setIsAuthed(true);
      // Fetch tenant in parallel-but-tolerant — a 404 here is OK
      // (user exists but tenant not yet created → onboarding gate
      // will catch it).
      try {
        const tenant = await authAPI.getTenant();
        setCurrentTenant(tenant);
        if (tenant) localStorage.setItem('nombase_tenant', JSON.stringify(tenant));
      } catch {
        setCurrentTenant(null);
      }
      // Kick off the sliding-expiry refresh loop. We do this AFTER
      // `setIsAuthed(true)` so a refresh-401 race (server killed our
      // session between getMe and refresh) correctly triggers the
      // unauth path instead of leaving us in a half-authed state.
      _runRefreshOnce(myGen);
    } catch {
      // Defensive — any unexpected throw reflects the unauthenticated
      // state cleanly. The inline dev-bootstrap retry above means we
      // only reach here if BOTH /auth/me AND the bootstrap retry
      // misbehaved in a way the inner try/catches missed.
      setCurrentUser(null);
      setCurrentTenant(null);
      setIsAuthed(false);
      try { localStorage.removeItem('nombase_tenant'); } catch { /* */ }
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadIdentity(); }, [loadIdentity]);

  // Tear down the refresh timer on unmount. (Logout / forced unauth
  // already clear it via `_clearRefreshTimer` inline; this is the
  // last-resort cleanup for the AuthProvider being unmounted, e.g.
  // a route-level error boundary swap.)
  useEffect(() => () => { _clearRefreshTimer(); }, []);

  // ── Sentry / PostHog identity sync — same payload shape as the
  //    Clerk era, just sourced from backend `currentUser` now.
  useEffect(() => {
    try {
      if (currentUser?.email) {
        Sentry.setUser({
          id:       currentUser.clerk_user_id || currentUser.id,
          email:    currentUser.email,
          username: currentUser.name || currentUser.email,
        });
        Sentry.setTag('tenant_id',   currentUser.tenant_id   || 'none');
        Sentry.setTag('tenant_type', currentUser.tenant_type || 'none');
        Sentry.setTag('role',        currentUser.role        || 'member');
      } else {
        Sentry.setUser(null);
      }
    } catch { /* never let observability break auth */ }
  }, [
    currentUser?.id, currentUser?.clerk_user_id,
    currentUser?.email, currentUser?.name,
    currentUser?.tenant_id, currentUser?.tenant_type, currentUser?.role,
  ]);

  useEffect(() => {
    try {
      const ident = currentUser?.clerk_user_id || currentUser?.id;
      if (ident) {
        posthog.identify(ident, {
          email:       currentUser.email || undefined,
          name:        currentUser.name  || undefined,
          tenant_id:   currentUser.tenant_id   || undefined,
          tenant_type: currentUser.tenant_type || undefined,
          role:        currentUser.role        || undefined,
        });
      } else if (!isAuthenticated) {
        posthog.reset?.();
      }
    } catch { /* never let analytics break auth */ }
  }, [
    currentUser?.id, currentUser?.clerk_user_id,
    currentUser?.email, currentUser?.name,
    currentUser?.tenant_id, currentUser?.tenant_type, currentUser?.role,
    isAuthenticated,
  ]);

  // ── Persona flags (brand-only MVP) ───────────────────────────────
  // The `personaResolution` shim now returns `isBrand: true` and
  // every supplier flag as `false`. We still destructure them so
  // any consumer of the legacy context shape keeps compiling — but
  // the supplier branches below are dead code.
  const { tenantType, isBrand } = resolvePersonaFlags({ currentUser, currentTenant });

  // Tenant-level admin role is intentionally not surfaced — system-
  // admin lives in the standalone Admin Portal.
  const isAdmin        = false;
  const isRequestReady = isBrand ? currentTenant?.is_request_ready : false;
  // `isSearchable` was a supplier-side public-marketplace flag. The
  // brand never reads it; left as a constant `false` for any stale
  // callsite (none today, but cheap to keep).
  const isSearchable   = false;

  const refreshUser = useCallback(async () => {
    try {
      const me = await authAPI.getMe();
      if (me?.id) setCurrentUser(me);
    } catch { /* */ }
  }, []);

  const refreshTenant = useCallback(async () => {
    try {
      const tenant = await authAPI.getTenant();
      setCurrentTenant(tenant);
      if (tenant) localStorage.setItem('nombase_tenant', JSON.stringify(tenant));
    } catch { /* */ }
  }, []);

  const logout = useCallback(async () => {
    // Stop any pending refresh BEFORE we tear down state so a
    // late-firing timer doesn't try to mint a new cookie against
    // an about-to-be-cleared session.
    refreshGenRef.current += 1;
    _clearRefreshTimer();
    // Backend: drop the `nombase_session` cookie if one was issued
    // by the dev_bootstrap fallback. Fail-safe — 404 / 500 still
    // proceeds to the Clerk sign-out + gateway redirect.
    try { await authAPI.logout?.(); } catch { /* */ }
    try {
      localStorage.removeItem('nombase_tenant');
      localStorage.removeItem('nombase_user');
      localStorage.removeItem('nombase_query_cache_v1');
    } catch { /* */ }
    setCurrentUser(null);
    setCurrentTenant(null);
    setIsAuthed(false);
    // Clerk removed (May 2026) — nothing to sign out of upstream.
    // The `nombase_session` cookie was cleared by the POST above;
    // we just bounce to `/` so AuthContext re-runs loadIdentity,
    // which mints a fresh dev_bootstrap session.
    navigateToSignIn('sign_out');
  }, []);

  const value = useMemo(() => ({
    // ── State ─────────────────────────────────────────────
    isAuthenticated,
    setIsAuthenticated: () => {},
    isLoading,
    authError,
    currentUser,
    setCurrentUser,
    currentTenant,
    setCurrentTenant,
    // ── Personas (brand-only MVP) ─────────────────────────
    // Supplier flags are still exported as `false` constants so any
    // stale callsite that destructures them safely no-ops. New code
    // should read `isBrand` only.
    tenantType,
    isBrand,
    isSupplier:          false,
    isFlavorHouse:       false,
    isPackagingSupplier: false,
    isCoManufacturer:    false,
    isAdmin,
    isRequestReady,
    isSearchable,
    unreadCount: 0,
    // ── Actions ───────────────────────────────────────────
    login:  async () => { navigateToSignIn('login_required'); },
    signup: async () => { navigateToSignIn('signup_required'); },
    logout,
    refreshTenant,
    refreshUser,
    // Expose loadIdentity so ClerkBridge can re-run the /api/auth/me
    // probe once Clerk has registered `getToken` into the token bridge.
    // Without this, the initial probe at mount fires BEFORE Clerk's
    // SDK is ready, returns 401 (no Authorization header yet), and
    // AuthContext is stuck in `isAuthenticated=false` even though
    // Clerk has signed the user in. ClerkBridge re-invokes this
    // single-shot when `isSignedIn` flips true.
    refreshIdentity: loadIdentity,
    // Phase 2 will reintroduce a real implementation if we add
    // tenant-org switching. Until then this is a no-op so existing
    // callers don't crash.
    setActiveOrganization: async () => {},
    getRequests: () => [],
    getUserNotifications: () => [],
  }), [
    isAuthenticated, isLoading, authError,
    currentUser, currentTenant,
    tenantType, isBrand, isAdmin,
    isRequestReady, isSearchable,
    logout, refreshTenant, refreshUser, loadIdentity,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
